/**
 * Slice 59 — photo groups ("spots") inside the onboarding walk.
 * Where you are is a mode: set the spot once, then keep tapping items. The Spot bar sits above the shutter;
 * Confirm is grouped by spot, with Move on every row.
 */
import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type Spot = {
  id: string;
  name: string;
  /** Local blob URL while on the phone. */
  localUrl: string;
  photoUrl: string | null;
};

export const SPOT_SUGGESTIONS = [
  "Cutlery drawer",
  "Plates and bowls shelf",
  "Pots and pans cupboard",
  "Utensil drawer",
  "Under the sink",
  "Towel shelf",
  "Wardrobe",
  "Bedside table",
];

export const countText = (n: number) => `${n} item${n === 1 ? "" : "s"}`;

/* ------------------------------------------------------------------ Spot bar */

export function SpotBar({
  spot,
  count,
  toRetake,
  onOpen,
  onWhole,
}: {
  spot: Spot | null;
  count: number;
  toRetake: number;
  onOpen: () => void;
  onWhole: () => void;
}) {
  const retake = toRetake > 0 ? ` · ${toRetake} to retake` : "";
  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          "flex h-16 items-center gap-3 rounded-[16px] px-3",
          spot ? "border-2 border-[#24a89a] bg-[rgba(10,48,44,0.88)]" : "bg-[rgba(15,23,32,0.72)]",
        )}
      >
        <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-label="Spot">
          {spot ? (
            <img src={spot.localUrl} alt="" className="h-10 w-10 shrink-0 rounded-[9px] border border-white/40 object-cover" />
          ) : (
            <span className="h-10 w-10 shrink-0 rounded-[9px] border-2 border-dashed border-white/45" />
          )}
          <span className="flex min-w-0 flex-col">
            <span className="text-[10.5px] font-bold tracking-[1px] text-[#8fd8cf]">
              SPOT{retake ? <span className="text-[#f2b9b2]">{retake.toUpperCase()}</span> : null}
            </span>
            <span className="truncate text-[15px] font-bold leading-tight text-white">
              {spot ? spot.name : "Whole room"} · {countText(count)}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="flex h-11 shrink-0 items-center rounded-[12px] bg-white px-3.5 text-[14.5px] font-bold text-[#0f1720]"
        >
          {spot ? "Change" : "New spot"}
        </button>
      </div>
      {spot ? (
        <button
          type="button"
          onClick={onWhole}
          className="flex h-11 w-full items-center justify-center rounded-[13px] bg-[rgba(15,23,32,0.72)] text-[14.5px] font-bold text-white"
        >
          Back to whole room
        </button>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- Mode toggle */

/** Out in the open / Inside something. Inside = photo the open spot first, then everything shot joins it. */
export function ModeToggle({ inside, onChange }: { inside: boolean; onChange: (inside: boolean) => void }) {
  return (
    <div className="relative flex h-[52px] rounded-[26px] bg-[rgba(15,23,32,0.72)] p-1">
      <span
        className="absolute bottom-1 top-1 w-[calc(50%-4px)] rounded-[22px] bg-white transition-transform duration-300"
        style={{
          transform: inside ? "translateX(100%)" : "translateX(0)",
          transitionTimingFunction: "cubic-bezier(.45,1.45,.45,1)",
        }}
      />
      {[
        { id: false, label: "Out in the open" },
        { id: true, label: "Inside something" },
      ].map((m) => (
        <button
          key={String(m.id)}
          type="button"
          onClick={() => onChange(m.id)}
          className={cn(
            "relative z-10 flex flex-1 items-center justify-center text-[15px] font-bold transition-colors",
            inside === m.id ? "text-[#0f1720]" : "text-white",
          )}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

/** Shown while a spot is open: items you shoot now join it. */
export function SpotOpenBar({
  spot,
  count,
  toRetake,
  onDone,
}: {
  spot: Spot;
  count: number;
  toRetake: number;
  onDone: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-[16px] border-2 border-[#24a89a] bg-[rgba(10,48,44,0.88)] p-2.5 pl-3">
      <img src={spot.localUrl} alt="" className="h-10 w-10 shrink-0 rounded-[9px] border border-white/40 object-cover" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[15px] font-bold leading-tight text-white">Spot: {spot.name}, open</span>
        <span className="text-[12.5px] font-medium text-[#8fd8cf]">
          {countText(count)}
          {toRetake > 0 ? <span className="text-[#f2b9b2]"> · {toRetake} to retake</span> : null}
        </span>
      </span>
      <button
        type="button"
        onClick={onDone}
        className="flex h-11 shrink-0 items-center rounded-[12px] bg-white px-3.5 text-[14px] font-bold text-[#0f1720]"
      >
        Done with this spot
      </button>
    </div>
  );
}

/** After the wide photo: name the spot, or pick a saved spot to replace its photo. */
export function SpotNameSheet({
  localUrl,
  roomName,
  usedNames,
  saved = [],
  hasCandidates,
  onUse,
  onRetake,
}: {
  localUrl: string;
  roomName: string;
  usedNames: string[];
  /** Spots already saved for this room: pick one to replace its photo. */
  saved?: { name: string; photoUrl: string | null; itemCount: number }[];
  /** There are saved items to tick into the spot on the next step. */
  hasCandidates: boolean;
  onUse: (name: string) => void;
  onRetake: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [chip, setChip] = useState<string | null>(null);
  const used = new Set(usedNames.map((n) => n.toLowerCase()));
  const savedFree = saved.filter((sv) => !used.has(sv.name.toLowerCase()));
  const free = SPOT_SUGGESTIONS.filter(
    (n) => !used.has(n.toLowerCase()) && !saved.some((sv) => sv.name.toLowerCase() === n.toLowerCase()),
  ).slice(0, 6);
  const name = (typed.trim() || chip || "").trim();
  const savedMatch = savedFree.find((sv) => sv.name.toLowerCase() === name.toLowerCase()) ?? null;
  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-[#1a2228]">
      <img src={localUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="flex-1" />
      <div className="relative z-10 max-h-[78%] overflow-y-auto rounded-t-[26px] bg-white px-5 pb-6 pt-5">
        <div className="text-[11px] font-bold tracking-[0.9px] text-[#15776d]">{roomName.toUpperCase()} · SPOT PHOTO</div>
        <div className="mt-1 text-[22px] font-bold tracking-[-0.4px] text-[#0f1720]">What is this spot called?</div>
        {savedFree.length > 0 ? (
          <div className="mt-3">
            <div className="mb-1.5 text-[11px] font-bold tracking-[0.9px] text-[#8494a2]">Saved spots, replaces their photo</div>
            <div className="flex flex-wrap gap-2">
              {savedFree.map((sv) => (
                <button
                  key={sv.name}
                  type="button"
                  onClick={() => {
                    setChip(chip === sv.name ? null : sv.name);
                    setTyped("");
                  }}
                  className={cn(
                    "flex min-h-[44px] items-center gap-2 rounded-[12px] border-[1.5px] py-1 pl-1.5 pr-3.5 text-[14.5px] font-bold",
                    chip === sv.name ? "border-[#24a89a] bg-[#e4f2f0] text-[#0d453f]" : "border-[#e6edf1] bg-white text-[#475569]",
                  )}
                >
                  {sv.photoUrl ? (
                    <img src={sv.photoUrl} alt="" className="h-8 w-8 shrink-0 rounded-[8px] object-cover" />
                  ) : (
                    <span className="h-8 w-8 shrink-0 rounded-[8px] border-2 border-dashed border-[#cfdbe2]" />
                  )}
                  {sv.name}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mt-3">
          <div className="mb-1.5 text-[11px] font-bold tracking-[0.9px] text-[#8494a2]">New spot</div>
          <div className="flex flex-wrap gap-2">
            {free.map((n) => (
              <Chip
                key={n}
                label={n}
                on={chip === n}
                onClick={() => {
                  setChip(chip === n ? null : n);
                  setTyped("");
                }}
              />
            ))}
          </div>
        </div>
        <input
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            setChip(null);
          }}
          placeholder="Or type a name"
          aria-label="Spot name"
          className="mt-3 h-[52px] w-full rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#f8fafb] px-4 font-medium text-[#0f1720] outline-none focus:border-[#24a89a]"
          style={{ fontSize: 16 }}
        />
        {savedMatch ? (
          <div className="mt-3 flex items-start gap-2.5 rounded-[14px] bg-[#e8f4f1] px-3.5 py-3">
            <span className="mt-0.5 text-[16px] text-[#15776d]">↻</span>
            <div>
              <div className="text-[14.5px] font-bold text-[#0d453f]">Replaces the photo of {savedMatch.name}</div>
              <div className="text-[12.5px] font-medium text-[#3b6a63]">
                {savedMatch.photoUrl ? "Its current photo is replaced." : "It has no photo yet."} Its {savedMatch.itemCount} item
                {savedMatch.itemCount === 1 ? "" : "s"} stay in it.
              </div>
            </div>
          </div>
        ) : null}
        <div className="mt-4 flex gap-2.5">
          <button type="button" onClick={onRetake} className="flex h-14 flex-1 items-center justify-center rounded-[15px] bg-[#e2eeed] text-[16px] font-bold text-[#15776d]">
            Retake
          </button>
          <button
            type="button"
            disabled={!name}
            onClick={() => onUse(name)}
            className={cn(
              "flex h-14 flex-[1.7] items-center justify-center rounded-[15px] text-[16.5px] font-bold",
              name ? "bg-[#15776d] text-white" : "bg-[#e6edf1] text-[#9aa8b2]",
            )}
          >
            {!name ? "Pick a name" : savedMatch ? "Replace photo" : hasCandidates ? "Next: what lives here" : "Use name, start tapping items"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Inside something: pick a spot you already made to add more to it, or start a new one. */
export function SpotPickSheet({
  spots,
  countOf,
  onPick,
  onNew,
  onCancel,
}: {
  spots: Spot[];
  countOf: (id: string) => number;
  onPick: (id: string) => void;
  onNew: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="absolute inset-0 z-30 flex flex-col justify-end">
      <button type="button" className="absolute inset-0 bg-[rgba(15,23,32,0.5)]" aria-label="Close" onClick={onCancel} />
      <div className="relative max-h-[80%] overflow-y-auto rounded-t-[26px] bg-white px-5 pb-6 pt-5">
        <div className="text-[22px] font-bold tracking-[-0.4px] text-[#0f1720]">Which spot?</div>
        <div className="mt-3 flex flex-col gap-2">
          {spots.map((sp) => (
            <button
              key={sp.id}
              type="button"
              onClick={() => onPick(sp.id)}
              className="flex h-16 items-center gap-3 rounded-[16px] bg-[#f4f7f9] px-3 text-left"
            >
              <img src={sp.localUrl} alt="" className="h-11 w-11 shrink-0 rounded-[10px] object-cover" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[16px] font-bold text-[#0f1720]">{sp.name}</span>
                <span className="block text-[13px] font-medium text-[#8494a2]">Add more · {countText(countOf(sp.id))}</span>
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onNew}
          className="mt-3 flex h-14 w-full items-center justify-center rounded-[15px] bg-[#15776d] text-[17px] font-bold text-white"
        >
          New spot
        </button>
        <button type="button" onClick={onCancel} className="mt-1 flex h-12 w-full items-center justify-center text-[15.5px] font-bold text-[#15776d]">
          Cancel
        </button>
      </div>
    </div>
  );
}

/** After a spot photo: tick the items already captured in this room that live in it. No retaking item photos. */
export type ExistingPick = {
  key: string;
  name: string;
  photoUrl: string | null;
  kind: "unit" | "supply" | "appliance";
  /** Name of the spot the item is in already (a different one), when it is in one. */
  inSpot?: string | null;
};

const kindWord = (k: ExistingPick["kind"]) => (k === "unit" ? "Unit item" : k === "supply" ? "Supply" : "Appliance");

export function ExistingItemsSheet({
  spotName,
  roomName,
  items,
  initialPicked,
  onUse,
  onSkip,
}: {
  spotName: string;
  roomName: string;
  items: ExistingPick[];
  initialPicked: string[];
  onUse: (keys: string[]) => void;
  onSkip: () => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initialPicked));
  const toggle = (k: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const free = items.filter((i) => !i.inSpot);
  const elsewhere = items.filter((i) => i.inSpot);
  const Thumb = ({ it }: { it: ExistingPick }) =>
    it.photoUrl ? (
      <img src={it.photoUrl} alt="" className="h-10 w-10 shrink-0 rounded-[10px] object-cover" />
    ) : (
      <span className="h-10 w-10 shrink-0 rounded-[10px] bg-[#e6edf1]" />
    );
  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-[#f4f7f9]">
      <div className="px-5 pb-2 pt-5">
        <div className="text-[13px] font-bold text-[#64748b]">
          {spotName} · {roomName}
        </div>
        <div className="mt-0.5 text-[26px] font-bold tracking-[-0.5px] text-[#0f1720]">What lives here</div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3">
        <div className="px-1 pb-1.5 pt-2 text-[11px] font-bold tracking-[0.9px] text-[#8494a2]">NOT IN A SPOT YET · {free.length}</div>
        <div className="overflow-hidden rounded-[18px] bg-white">
          {free.map((it, i) => {
            const on = picked.has(it.key);
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => toggle(it.key)}
                className={cn("flex min-h-[58px] w-full items-center gap-3 px-3.5 text-left", i > 0 && "border-t border-[#eef2f5]")}
              >
                <span
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] border-2 text-[14px] font-bold",
                    on ? "border-[#15776d] bg-[#15776d] text-white" : "border-[#c5d1d9] bg-white text-transparent",
                  )}
                >
                  ✓
                </span>
                <Thumb it={it} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[16px] font-bold text-[#0f1720]">{it.name}</span>
                  <span className="block text-[12px] font-bold text-[#8494a2]">{kindWord(it.kind)}</span>
                </span>
              </button>
            );
          })}
          {free.length === 0 ? <div className="px-4 py-4 text-[14px] font-medium text-[#8494a2]">Everything is in a spot already.</div> : null}
        </div>
        {elsewhere.length > 0 ? (
          <>
            <div className="px-1 pb-1.5 pt-4 text-[11px] font-bold tracking-[0.9px] text-[#8494a2]">ALREADY IN A SPOT</div>
            <div className="overflow-hidden rounded-[18px] bg-white">
              {elsewhere.map((it, i) => {
                const on = picked.has(it.key);
                return (
                  <div key={it.key} className={cn("flex min-h-[58px] items-center gap-3 px-3.5", i > 0 && "border-t border-[#eef2f5]", !on && "opacity-60")}>
                    <Thumb it={it} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[16px] font-bold text-[#0f1720]">{it.name}</span>
                      <span className="block truncate text-[12px] font-bold text-[#8494a2]">{on ? `Moving here from ${it.inSpot}` : it.inSpot}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => toggle(it.key)}
                      className={cn("h-9 shrink-0 rounded-[10px] px-3 text-[13.5px] font-bold", on ? "bg-[#e2eeed] text-[#15776d]" : "bg-[#f1f5f7] text-[#475569]")}
                    >
                      {on ? "Moving ✓" : "Move here"}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
      <div className="bg-white px-4 pb-5 pt-3">
        <button type="button" onClick={() => onUse([...picked])} className="flex h-14 w-full items-center justify-center rounded-[15px] bg-[#15776d] text-[17px] font-bold text-white">
          {picked.size > 0 ? `Add ${picked.size} to this spot` : "Nothing in here yet"}
        </button>
        <button type="button" onClick={onSkip} className="mt-1 flex h-12 w-full items-center justify-center text-[15.5px] font-bold text-[#64748b]">
          Skip
        </button>
      </div>
    </div>
  );
}

/** B8a: the spot is saved. Add another spot, or finish. */
export function SpotSavedScreen({
  localUrl,
  name,
  roomName,
  itemNames,
  stillOpen,
  onAnother,
  onDone,
}: {
  localUrl: string;
  name: string;
  roomName: string;
  itemNames: string[];
  stillOpen: number;
  onAnother: () => void;
  onDone: () => void;
}) {
  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-[#f4f7f9]">
      <div className="flex items-center gap-3 px-4 pt-5 text-[15px] font-bold text-[#475569]">{roomName}</div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 pt-3">
        <div className="rounded-[22px] bg-white p-3">
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[16px] bg-[#e6edf1]">
            <img src={localUrl} alt="" className="h-full w-full object-cover" />
            <span className="absolute left-3 top-3 rounded-full bg-[#15776d] px-3 py-1.5 text-[12.5px] font-bold text-white">✓ Saved</span>
          </div>
          <div className="px-1 pb-1 pt-3">
            <div className="text-[24px] font-bold tracking-[-0.4px] text-[#0f1720]">{name}</div>
            {itemNames.length > 0 ? (
              <div className="mt-2.5 flex flex-wrap gap-2">
                {itemNames.map((n, i) => (
                  <span key={`${n}-${i}`} className="rounded-full bg-[#f1f5f7] px-3.5 py-1.5 text-[14px] font-bold text-[#0f1720]">
                    {n}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="mt-3 text-[13.5px] font-medium text-[#8494a2]">
              {stillOpen} item{stillOpen === 1 ? "" : "s"} still in the open
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white px-4 pb-5 pt-3">
        <button type="button" onClick={onAnother} className="flex h-14 w-full items-center justify-center rounded-[15px] bg-[#15776d] text-[17px] font-bold text-white">
          Add another spot
        </button>
        <button type="button" onClick={onDone} className="mt-1 flex h-12 w-full items-center justify-center text-[16px] font-bold text-[#0f1720]">
          Done
        </button>
      </div>
    </div>
  );
}

/** B8b: the walk added spots only. */
export function SpotsDoneScreen({
  roomName,
  photosTaken,
  rows,
  onSee,
}: {
  roomName: string;
  photosTaken: number;
  rows: { key: string; name: string; items: number; photoUrl: string | null; note: string }[];
  onSee: () => void;
}) {
  return (
    <div className="flex h-full flex-col bg-[#f4f7f9]">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 pt-10 text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#15776d] text-[30px] text-white">✓</span>
        <div className="mt-4 text-[28px] font-bold tracking-[-0.6px] text-[#0f1720]">{roomName} spots are set</div>
        <div className="mt-1 text-[14.5px] font-medium text-[#64748b]">
          {photosTaken} spot photo{photosTaken === 1 ? "" : "s"} taken. No item photos retaken.
        </div>
        <div className="mt-5 overflow-hidden rounded-[20px] bg-white text-left">
          {rows.map((r, i) => (
            <div key={r.key} className={cn("flex items-center gap-3 px-3.5 py-3", i > 0 && "border-t border-[#eef2f5]")}>
              {r.photoUrl ? (
                <img src={r.photoUrl} alt="" className="h-11 w-11 shrink-0 rounded-[10px] object-cover" />
              ) : (
                <span className="h-11 w-11 shrink-0 rounded-[10px] border-2 border-dashed border-[#cfdbe2]" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15.5px] font-bold text-[#0f1720]">{r.name}</div>
                <div className="text-[12.5px] font-medium text-[#8494a2]">
                  {r.items} item{r.items === 1 ? "" : "s"}
                </div>
              </div>
              <span className={cn("text-[13px] font-bold", r.note === "Kept" ? "text-[#8494a2]" : "text-[#15776d]")}>{r.note}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white px-4 pb-5 pt-3">
        <button type="button" onClick={onSee} className="flex h-14 w-full items-center justify-center rounded-[15px] bg-[#15776d] text-[17px] font-bold text-white">
          See {roomName} ›
        </button>
      </div>
    </div>
  );
}

/** B9: the room summary: spot cards with their items; whatever is left stays In the open. */
export function RoomSummaryScreen({
  roomName,
  total,
  spots,
  openNames,
  onDone,
}: {
  roomName: string;
  total: number;
  spots: { key: string; name: string; photoUrl: string | null; itemNames: string[] }[];
  openNames: string[];
  onDone: () => void;
}) {
  return (
    <div className="flex h-full flex-col bg-[#f4f7f9]">
      <div className="px-5 pb-2 pt-5">
        <div className="text-[26px] font-bold tracking-[-0.5px] text-[#0f1720]">{roomName}</div>
        <div className="text-[13.5px] font-medium text-[#64748b]">
          {total} items · {spots.length} spot{spots.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3">
        <div className="px-1 pb-1.5 pt-2 text-[16px] font-bold text-[#0f1720]">Spots</div>
        <div className="grid grid-cols-2 gap-2.5">
          {spots.map((sp) => (
            <div key={sp.key} className="flex flex-col gap-2 rounded-[18px] bg-white p-2.5">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-[12px] bg-[#e6edf1]">
                {sp.photoUrl ? <img src={sp.photoUrl} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center rounded-[12px] border-2 border-dashed border-[#cfdbe2] text-[11.5px] font-bold text-[#8494a2]">No photo yet</div>}
              </div>
              <div className="px-0.5">
                <div className="text-[14.5px] font-bold leading-tight text-[#0f1720]">{sp.name}</div>
                <div className="mt-0.5 text-[12px] font-medium leading-snug text-[#8494a2]">{sp.itemNames.join(", ")}</div>
              </div>
            </div>
          ))}
        </div>
        {openNames.length > 0 ? (
          <>
            <div className="px-1 pb-1.5 pt-4 text-[16px] font-bold text-[#0f1720]">In the open</div>
            <div className="flex flex-wrap gap-2">
              {openNames.map((n, i) => (
                <span key={`${n}-${i}`} className="rounded-full bg-white px-3.5 py-1.5 text-[14px] font-bold text-[#0f1720]">
                  {n}
                </span>
              ))}
            </div>
          </>
        ) : null}
      </div>
      <div className="bg-white px-4 pb-5 pt-3">
        <button type="button" onClick={onDone} className="flex h-14 w-full items-center justify-center rounded-[15px] bg-[#15776d] text-[17px] font-bold text-white">
          Done
        </button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- Sheets */

function Sheet({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div className="absolute inset-0 z-30 flex flex-col justify-end">
      <button type="button" className="absolute inset-0 bg-[rgba(15,23,32,0.5)]" aria-label="Close" onClick={onClose} />
      <div className="relative max-h-[88%] overflow-y-auto rounded-t-[26px] bg-white px-5 pb-6 pt-5">{children}</div>
    </div>
  );
}

function Chip({ label, on, onClick }: { label: string; on?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-11 items-center rounded-[12px] border-[1.5px] px-3.5 text-[14.5px] font-bold",
        on ? "border-[#24a89a] bg-[#e4f2f0] text-[#0d453f]" : "border-[#e6edf1] bg-white text-[#475569]",
      )}
    >
      {label}
    </button>
  );
}

export function SpotSheet({
  mode,
  roomName,
  spots,
  currentId,
  wholeCount,
  countOf,
  onPickSpot,
  onPickWhole,
  onNext,
  onCancel,
}: {
  mode: "new" | "change";
  roomName: string;
  spots: Spot[];
  currentId: string | null;
  wholeCount: number;
  countOf: (id: string) => number;
  onPickSpot: (id: string) => void;
  onPickWhole: () => void;
  onNext: (name: string) => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [chip, setChip] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const name = (typed.trim() || chip || "").trim();
  const used = new Set(spots.map((s) => s.name.toLowerCase()));
  const free = SPOT_SUGGESTIONS.filter((n) => !used.has(n.toLowerCase()));
  const shown = mode === "change" && !more ? free.slice(0, 4) : free;
  return (
    <Sheet onClose={onCancel}>
      <div className="text-[22px] font-bold tracking-[-0.4px] text-[#0f1720]">{mode === "new" ? "New spot" : "Change spot"}</div>
      {mode === "new" ? (
        <div className="mt-1 text-[14px] font-medium text-[#64748b]">The drawer, shelf or cupboard you’re about to open.</div>
      ) : (
        <>
          <div className="mb-2 mt-3 text-[11px] font-bold tracking-[0.9px] text-[#8494a2]">IN THIS WALK</div>
          <div className="flex flex-col gap-2">
            {spots.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onPickSpot(s.id)}
                className={cn(
                  "flex h-14 items-center gap-3 rounded-[14px] border-[1.5px] px-3",
                  s.id === currentId ? "border-[#24a89a] bg-[#f1f9f8]" : "border-[#e6edf1] bg-white",
                )}
              >
                <img src={s.localUrl} alt="" className="h-9 w-9 shrink-0 rounded-[8px] object-cover" />
                <span className="min-w-0 flex-1 truncate text-left text-[15px] font-bold text-[#0f1720]">
                  {s.name} · {countText(countOf(s.id))}
                </span>
                {s.id === currentId ? <span className="text-[12.5px] font-bold text-[#15776d]">Here now</span> : null}
              </button>
            ))}
            <button
              type="button"
              onClick={onPickWhole}
              className={cn(
                "flex h-14 items-center gap-3 rounded-[14px] border-[1.5px] px-3",
                currentId === null ? "border-[#24a89a] bg-[#f1f9f8]" : "border-[#e6edf1] bg-white",
              )}
            >
              <span className="h-9 w-9 shrink-0 rounded-[8px] border-2 border-dashed border-[#c5d1d9]" />
              <span className="min-w-0 flex-1 truncate text-left text-[15px] font-bold text-[#0f1720]">
                Whole room · {countText(wholeCount)}
              </span>
              {currentId === null ? <span className="text-[12.5px] font-bold text-[#15776d]">Here now</span> : null}
            </button>
          </div>
          <div className="mb-2 mt-4 text-[11px] font-bold tracking-[0.9px] text-[#8494a2]">OR A NEW SPOT</div>
        </>
      )}
      <input
        value={typed}
        onChange={(e) => {
          setTyped(e.target.value);
          setChip(null);
        }}
        placeholder="Type a name"
        aria-label="Spot name"
        className="mt-3 h-[52px] w-full rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#f8fafb] px-4 text-[16px] font-medium text-[#0f1720] outline-none focus:border-[#24a89a]"
        style={{ fontSize: 16 }}
      />
      {mode === "new" ? <div className="mb-2 mt-4 text-[11px] font-bold tracking-[0.9px] text-[#8494a2]">SUGGESTIONS</div> : <div className="mt-3" />}
      <div className="flex flex-wrap gap-2">
        {shown.map((n) => (
          <Chip
            key={n}
            label={n}
            on={chip === n}
            onClick={() => {
              setChip(chip === n ? null : n);
              setTyped("");
            }}
          />
        ))}
        {mode === "change" && !more && free.length > 4 ? <Chip label="More…" onClick={() => setMore(true)} /> : null}
      </div>
      {mode === "new" || name ? (
        <button
          type="button"
          disabled={!name}
          onClick={() => onNext(name)}
          className={cn(
            "mt-5 flex h-14 w-full items-center justify-center rounded-[15px] text-[17px] font-bold",
            name ? "bg-[#24a89a] text-white" : "bg-[#e6edf1] text-[#9aa8b2]",
          )}
        >
          Next: photo this spot
        </button>
      ) : null}
      <button type="button" onClick={onCancel} className="mt-1 flex h-12 w-full items-center justify-center text-[15.5px] font-bold text-[#15776d]">
        Cancel
      </button>
      <div className="sr-only">{roomName}</div>
    </Sheet>
  );
}

/* ----------------------------------------------------- Photo this spot */

export function SpotShoot({
  name,
  busy,
  onShoot,
  onBack,
}: {
  name: string | null;
  busy: boolean;
  onShoot: () => void;
  onBack: () => void;
}) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col px-[18px] pb-6 pt-[22px]">
      {name ? (
        <div className="pointer-events-none flex h-[34px] items-center self-start rounded-[17px] bg-[rgba(15,23,32,0.55)] px-3.5 text-[13px] font-bold text-white">
          {name}
        </div>
      ) : null}
      <div className="mt-3 rounded-[16px] bg-[rgba(15,23,32,0.82)] px-4 py-3.5">
        <div className="text-[10.5px] font-bold tracking-[1px] text-[#8fd8cf]">REFERENCE PHOTO</div>
        <div className="text-[22px] font-bold tracking-[-0.4px] text-white">{name || "Open it, then take one wide photo"}</div>
        <div className="mt-1 text-[14px] font-medium leading-snug text-white/85">
          Get everything in the frame, the way the cleaner should leave it.
        </div>
      </div>
      <div className="pointer-events-none mx-1 my-4 flex-1 rounded-[18px] border-2 border-white/60" />
      <button
        type="button"
        disabled={busy}
        onClick={onShoot}
        className="flex h-[62px] w-full items-center justify-center gap-2.5 rounded-[16px] bg-[#24a89a] text-[18px] font-bold text-white disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="h-5 w-5 rounded-full border-2 border-white" />}
        Photo this spot
      </button>
      <button
        type="button"
        onClick={onBack}
        className="mt-2.5 flex h-[52px] w-full items-center justify-center rounded-[14px] bg-[rgba(15,23,32,0.78)] text-[16px] font-bold text-white"
      >
        Back
      </button>
    </div>
  );
}

/* ------------------------------------------------- Keep or retake */

export function SpotReview({
  name,
  localUrl,
  onUse,
  onRetake,
}: {
  name: string;
  localUrl: string;
  onUse: () => void;
  onRetake: () => void;
}) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-[#1a2228]">
      <img src={localUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="relative z-10 px-[18px] pt-[22px]">
        <span className="inline-flex h-[34px] items-center rounded-[17px] bg-[rgba(15,23,32,0.6)] px-3.5 text-[13px] font-bold text-white">
          {name}
        </span>
      </div>
      <div className="flex-1" />
      <div className="relative z-10 rounded-t-[26px] bg-white px-5 pb-6 pt-5">
        <div className="text-[21px] font-bold tracking-[-0.4px] text-[#0f1720]">Is everything in?</div>
        <div className="mt-1 text-[14px] font-medium text-[#64748b]">The cleaner matches this photo after each clean.</div>
        <button type="button" onClick={onUse} className="mt-4 flex h-14 w-full items-center justify-center rounded-[15px] bg-[#24a89a] text-[17px] font-bold text-white">
          Use photo, start tapping items
        </button>
        <button type="button" onClick={onRetake} className="mt-2 flex h-14 w-full items-center justify-center rounded-[15px] border-[1.5px] border-[#e6edf1] bg-white text-[16.5px] font-bold text-[#0f1720]">
          Retake
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------ Confirm, grouped */

export type ConfirmRow = {
  id: string;
  name: string;
  localUrl: string;
  meta: string;
  kindLabel: string;
  isUnit: boolean;
  spotId: string | null;
};

export function MoveSheet({
  row,
  spots,
  onPick,
  onCancel,
}: {
  row: ConfirmRow;
  spots: Spot[];
  onPick: (spotId: string | null) => void;
  onCancel: () => void;
}) {
  return (
    <Sheet onClose={onCancel}>
      <div className="flex items-center gap-3">
        <img src={row.localUrl} alt="" className="h-11 w-11 shrink-0 rounded-[10px] object-cover" />
        <div className="text-[21px] font-bold tracking-[-0.4px] text-[#0f1720]">Move {row.name} to</div>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        {spots.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onPick(s.id)}
            className={cn(
              "flex h-14 items-center justify-between rounded-[14px] border-[1.5px] px-4 text-[16px] font-bold",
              row.spotId === s.id ? "border-[#24a89a] bg-[#f1f9f8] text-[#0f1720]" : "border-[#e6edf1] bg-white text-[#0f1720]",
            )}
          >
            {s.name}
            {row.spotId === s.id ? <span className="text-[12.5px] font-bold text-[#15776d]">In it now</span> : null}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPick(null)}
          className={cn(
            "flex h-14 items-center justify-between rounded-[14px] border-[1.5px] px-4 text-[16px] font-bold",
            row.spotId === null ? "border-[#24a89a] bg-[#f1f9f8]" : "border-[#e6edf1] bg-white",
          )}
        >
          Whole room
          {row.spotId === null ? <span className="text-[12.5px] font-bold text-[#15776d]">In it now</span> : null}
        </button>
      </div>
      <button type="button" onClick={onCancel} className="mt-2 flex h-12 w-full items-center justify-center text-[15.5px] font-bold text-[#15776d]">
        Cancel
      </button>
    </Sheet>
  );
}

function RowButton({ row, onOpen, onMove, showKind }: { row: ConfirmRow; onOpen: () => void; onMove?: () => void; showKind: boolean }) {
  return (
    <div className="flex items-center gap-3 border-t border-[#f1f5f7] px-3.5 py-3">
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <img src={row.localUrl} alt="" className="h-[48px] w-[48px] shrink-0 rounded-[11px] object-cover" />
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-bold leading-tight text-[#0f1720]">{row.name}</span>
          <span className="block text-[12.5px] font-medium text-[#8494a2]">{row.meta}</span>
        </span>
      </button>
      {showKind ? (
        <div className="flex h-[30px] shrink-0 items-center rounded-[10px] border-[1.5px] border-[#cfe6e2] bg-[#e4f2f0] px-2.5 text-[12.5px] font-bold text-[#0d453f]">
          {row.kindLabel}
        </div>
      ) : null}
      {onMove ? (
        <button
          type="button"
          onClick={onMove}
          className="flex h-11 shrink-0 items-center rounded-[12px] bg-[#e4f2f0] px-4 text-[14.5px] font-bold text-[#0d453f]"
        >
          Move
        </button>
      ) : null}
    </div>
  );
}

export function ConfirmGrouped({
  room,
  rows,
  spots,
  importing,
  onOpenRow,
  onMoveRow,
  onRetakeSpot,
  onRemoveSpot,
  onAddItems,
  onImport,
  onBack,
}: {
  room: string;
  rows: ConfirmRow[];
  spots: Spot[];
  importing: boolean;
  onOpenRow: (id: string) => void;
  onMoveRow: (row: ConfirmRow) => void;
  onRetakeSpot: (id: string) => void;
  onRemoveSpot: (id: string) => void;
  onAddItems: (id: string) => void;
  onImport: () => void;
  onBack: () => void;
}) {
  const hasSpots = spots.length > 0;
  const otherCount = (id: string | null) => rows.filter((r) => r.spotId === id && !r.isUnit).length;
  const emptySpots = spots.filter((s) => rows.filter((r) => r.spotId === s.id).length === 0).length;
  const wholeRows = rows.filter((r) => r.spotId === null);
  return (
    <div className="flex h-full flex-col bg-[#f4f7f9]">
      <div className="flex items-center gap-3 border-b border-[#e6edf1] bg-white px-[22px] pb-4 pt-5">
        <button type="button" onClick={onBack} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.4">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="min-w-0">
          <div className="text-[19px] font-bold tracking-[-0.3px]">
            {room} — {countText(rows.length)}
          </div>
          {hasSpots ? (
            <div className="text-[12.5px] font-medium text-[#8494a2]">
              {spots.length} spot{spots.length === 1 ? "" : "s"}
              {emptySpots ? ` · ${emptySpots} needs items` : ""}
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-[18px] pt-[18px]">
        <p className="mb-3 text-[13.5px] font-medium text-[#64748b]">
          {hasSpots ? "Grouped by spot. Tap Move if one landed in the wrong place." : "Glance and import. Tap a row to fix it."}
        </p>
        <div className="flex flex-col gap-3">
          {spots.map((s) => {
            const list = rows.filter((r) => r.spotId === s.id);
            const empty = list.length === 0;
            const others = otherCount(s.id);
            return (
              <div key={s.id} className="overflow-hidden rounded-[18px] bg-white">
                <div className="flex items-center gap-3 px-3.5 py-3">
                  <img src={s.localUrl} alt="" className="h-[52px] w-[52px] shrink-0 rounded-[12px] object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[16px] font-bold leading-tight text-[#0f1720]">{s.name}</div>
                    <div className="text-[12.5px] font-bold text-[#15776d]">
                      {countText(list.length)}
                      {others ? ` · ${others} go to Inventory or Appliances` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRetakeSpot(s.id)}
                    className="flex h-11 shrink-0 items-center rounded-[12px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 text-[14px] font-bold text-[#0f1720]"
                  >
                    Retake
                  </button>
                </div>
                {empty ? (
                  <div className="flex flex-col gap-1.5 border-t border-[#f3e4c4] bg-[#fffaf0] px-3.5 py-3">
                    <div className="text-[15px] font-bold text-[#8a5a00]">No items yet</div>
                    <div className="text-[13px] font-medium leading-snug text-[#8a5a00]/90">
                      The cleaner would get a photo prompt with nothing to check. Add items, or remove the spot.
                    </div>
                    <div className="mt-1 flex gap-2">
                      <button type="button" onClick={() => onRemoveSpot(s.id)} className="h-11 flex-1 rounded-[12px] border-[1.5px] border-[#e8c98f] bg-white text-[14.5px] font-bold text-[#8a5a00]">
                        Remove spot
                      </button>
                      <button type="button" onClick={() => onAddItems(s.id)} className="h-11 flex-1 rounded-[12px] bg-[#24a89a] text-[14.5px] font-bold text-white">
                        Add items
                      </button>
                    </div>
                  </div>
                ) : (
                  list.map((r) => <RowButton key={r.id} row={r} showKind={false} onOpen={() => onOpenRow(r.id)} onMove={() => onMoveRow(r)} />)
                )}
              </div>
            );
          })}
          {wholeRows.length > 0 || !hasSpots ? (
            <div className="overflow-hidden rounded-[18px] bg-white">
              <div className="flex items-center gap-3 px-3.5 py-3">
                <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[12px] border-2 border-dashed border-[#cfdbe2] text-center text-[10.5px] font-bold leading-tight text-[#8494a2]">
                  Room
                  <br />
                  photo
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[16px] font-bold leading-tight text-[#0f1720]">Whole room</div>
                  <div className="text-[12.5px] font-medium text-[#8494a2]">
                    {countText(wholeRows.length)} · on the room photo
                  </div>
                </div>
              </div>
              {wholeRows.map((r) => (
                <RowButton
                  key={r.id}
                  row={r}
                  showKind={!hasSpots}
                  onOpen={() => onOpenRow(r.id)}
                  onMove={hasSpots ? () => onMoveRow(r) : undefined}
                />
              ))}
            </div>
          ) : null}
        </div>
        <p className="mb-6 mt-3 text-[12.5px] font-medium leading-snug text-[#8494a2]">
          {hasSpots
            ? `Only unit items are checked in a spot. Nothing is in Inventory until you import. Rooms other than ${room} aren’t touched.`
            : `Nothing is in Inventory until you import. Rooms other than ${room} aren’t touched.`}
        </p>
      </div>
      <div className="border-t border-[#e6edf1] bg-white px-[18px] pb-6 pt-3.5">
        <button
          type="button"
          disabled={!rows.length || importing}
          onClick={onImport}
          className="flex h-14 w-full items-center justify-center rounded-[16px] bg-[#24a89a] text-[17px] font-bold text-white shadow-[0_8px_20px_rgba(36,168,154,0.26)] disabled:opacity-40"
        >
          {importing ? <Loader2 className="h-5 w-5 animate-spin" /> : "Import this room"}
        </button>
        <button type="button" onClick={onBack} className="mt-2 flex h-[50px] w-full items-center justify-center rounded-[14px] text-[15px] font-bold text-[#15776d]">
          Back to camera
        </button>
      </div>
    </div>
  );
}
